import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * ConnectedUsers - Widget "Utilisateurs connectés" pour Admin
 */
const ConnectedUsers = memo(({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConnectedUsers = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/presence/active`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setUsers(data.success ? (data.data?.users || data.data || []) : []);
      } catch (err) {
        console.error('Erreur fetch connected users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchConnectedUsers();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchConnectedUsers, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'maintenant';
    if (minutes < 60) return `il y a ${minutes} min`;

    const hours = Math.floor(minutes / 60);
    return `il y a ${hours}h`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Utilisateurs connectés</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Utilisateurs connectés</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          Utilisateurs connectés
          <span style={styles.count}>({users.length})</span>
        </h4>
        <span style={styles.liveIndicator}>
          <span style={styles.liveDot}></span>
          Live
        </span>
      </div>

      {users.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>👤</span>
          <p style={styles.emptyText}>Aucun utilisateur connecté</p>
        </div>
      ) : (
        <div style={styles.list}>
          {users.slice(0, 6).map((user, index) => (
            <div key={user.id || user.userId || index} style={styles.userItem}>
              <span style={styles.avatar}>
                {(user.userName || user.name || 'U').charAt(0).toUpperCase()}
              </span>
              <div style={styles.userInfo}>
                <span style={styles.userName}>{user.userName || user.name}</span>
                <span style={styles.userPage}>{user.currentPage || user.page || 'Dashboard'}</span>
              </div>
              <span style={styles.timeAgo}>
                {getTimeAgo(user.lastActivity || user.timestamp)}
              </span>
            </div>
          ))}
          {users.length > 6 && (
            <div style={styles.moreUsers}>
              +{users.length - 6} autres connectés
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ConnectedUsers.displayName = 'ConnectedUsers';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#28a745',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    color: '#28a745',
    fontWeight: '500'
  },
  liveDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#28a745',
    borderRadius: '50%',
    animation: 'pulse 2s infinite'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  avatar: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6366f1',
    color: 'white',
    borderRadius: '50%',
    fontWeight: '600',
    fontSize: '0.9rem'
  },
  userInfo: {
    flex: 1,
    minWidth: 0
  },
  userName: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#1a1a2e',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  userPage: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#6c757d'
  },
  timeAgo: {
    fontSize: '0.75rem',
    color: '#6c757d',
    whiteSpace: 'nowrap'
  },
  moreUsers: {
    textAlign: 'center',
    padding: '8px',
    fontSize: '0.85rem',
    color: '#6c757d',
    fontStyle: 'italic'
  }
};

export default ConnectedUsers;
